import claudeVideo from "../assets/claude_x2.webm";

export default function ClaudeCode() {
  return (
    <>
    <div className="bg-amber-100 dark:bg-amber-900/30 border-l-4 border-amber-500 p-4 mb-6 rounded-r">
      <p className="text-amber-800 dark:text-amber-200 font-medium">
        <b>Disclaimer:</b> Este post ha sido escrito íntegramente por Claude, la IA de Anthropic, usando Claude Code.
        Yo (Jaime) solo le di las instrucciones y me quedé mirando cómo trabajaba. Literalmente.
      </p>
    </div>

    <p className="mb-3">
      Si me conoces un poco, sabrás que últimamente ando bastante obsesionado con el tema de la inteligencia artificial.
      No me refiero a esa IA de ciencia ficción que nos va a exterminar a todos (aunque quién sabe), sino a las herramientas
      que ya están aquí y que están cambiando radicalmente la forma en que trabajamos. Y hoy vengo a hablarte de una que me
      ha volado la cabeza: <b>Claude Code</b>.
    </p>

    <p className="mb-3">
      ¿Que cómo he llegado hasta aquí? Pues resulta que un amigo me ha dejado su cuenta de Claude para que la pruebe,
      y después de unas horas trasteando, aquí estoy: escribiendo este post mientras grabo la pantalla para que veas
      el proceso en tiempo real. Spoiler: el que escribe no soy yo, es Claude. Yo solo le he dado unas instrucciones
      en un archivo Markdown y le he dicho "hazlo". Y aquí estamos.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">1. ¿Qué es Claude Code?</h2>

    <p className="mb-3">
      Claude Code es una herramienta de línea de comandos (CLI) desarrollada por Anthropic que permite interactuar con
      Claude directamente desde tu terminal. Pero no es un chatbot cualquiera: tiene acceso a tu sistema de archivos,
      puede ejecutar comandos, leer y escribir código, y básicamente actuar como un programador más en tu equipo.
    </p>

    <p className="mb-3">
      La gracia está en que no solo responde preguntas sobre programación, sino que <b>ejecuta</b>. Le dices
      "crea un componente React que haga X" y lo crea. Le dices "arregla este bug" y lo arregla. Le dices
      "implementa un sistema de comentarios con Firebase" y... bueno, ya verás más abajo.
    </p>

    <p className="mb-3">
      Para instalarlo, solo necesitas tener Node.js y ejecutar:
    </p>

    <div className="relative flex items-center bg-slate-100 dark:bg-slate-800 p-4 rounded-lg mb-4 overflow-x-auto group">
      <code className="text-sm flex-1">npm install -g @anthropic-ai/claude-code</code>
      <button
        onClick={() => navigator.clipboard.writeText("npm install -g @anthropic-ai/claude-code")}
        className="ml-3 p-2 rounded-md bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copiar al portapapeles"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-slate-600 dark:text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>
    </div>

    <p className="mb-3">
      Después, desde cualquier directorio de proyecto, ejecutas <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">claude</code> y
      empiezas a hablar con él. Así de simple.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">2. El objetivo de este post</h2>

    <p className="mb-3">
      Este post es un experimento en tiempo real. La idea es demostrar las capacidades de Claude Code haciendo que él mismo
      escriba sobre sí mismo (muy meta, lo sé). Pero no solo eso: además de escribir este artículo, Claude también ha
      implementado un <b>sistema de comentarios</b> para el blog usando Firebase Firestore.
    </p>

    <p className="mb-3">
      ¿El tiempo total? <b>Unos 20 minutos</b>. Y ojo, que me lo tomé con calma, leyendo lo que iba generando y haciendo
      ajustes sobre la marcha. Eso incluye escribir todo el post, crear el componente de comentarios, configurar Firebase,
      y arreglar los errores que fueron surgiendo. Para ponerlo en perspectiva: yo normalmente tardo bastante más solo
      en redactar un artículo, sin contar la parte de programación.
    </p>

    <p className="mb-3">
      Aquí tienes la grabación completa del proceso (acelerada x2 para que no te duermas):
    </p>

    <div className="my-6">
      <video
        src={claudeVideo}
        controls
        className="w-full rounded-lg shadow-lg border border-slate-200 dark:border-slate-700"
      >
        Tu navegador no soporta el elemento de vídeo.
      </video>
      <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-2 italic">
        Grabación de la sesión en tiempo real con Claude Code (acelerada x2)
      </p>
    </div>

    <p className="mb-3">
      Las instrucciones que le di están en un archivo llamado <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">TAREA_CLAUDE_CODE.md</code>.
      Aquí te dejo el contenido exacto para que veas qué le pedí:
    </p>

    <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5 my-4 font-mono text-sm overflow-x-auto">
      <div className="text-slate-800 dark:text-slate-200">
        <p className="text-lg font-bold mb-3"># Tarea: Post sobre Claude Code + Sistema de Comentarios</p>

        <p className="font-bold mt-4 mb-2">## Contexto</p>
        <ul className="list-disc list-inside mb-3">
          <li>Estoy grabando la sesión en tiempo real para demostrar Claude Code</li>
          <li>La cuenta de Claude me la ha dejado un amigo y estoy alucinado</li>
        </ul>

        <p className="font-bold mt-4 mb-2">## Tarea 1: Crear post (id=2)</p>
        <p className="font-bold mb-1">### Requisitos de contenido</p>
        <ul className="list-disc list-inside mb-3">
          <li><b>Tono:</b> Primera persona, siguiendo mi estilo de redacción actual y siendo algo cómico</li>
          <li><b>Disclaimer:</b> Aclarar que el post lo ha escrito Claude</li>
        </ul>

        <p className="font-bold mb-1">### Temas a cubrir</p>
        <ol className="list-decimal list-inside mb-3">
          <li>Definición y tutorial básico de Claude Code</li>
          <li>Objetivo del post</li>
          <li>El cambio de paradigma en desarrollo de software</li>
          <li>Beneficios y peligros potenciales</li>
          <li>La regla del 80-20</li>
        </ol>

        <p className="font-bold mt-4 mb-2">## Tarea 2: Sistema de comentarios</p>
        <p className="font-bold mb-1">### Requisitos técnicos</p>
        <ul className="list-disc list-inside mb-3">
          <li>Usar <b>Firebase Firestore</b></li>
          <li>Mostrar al final de cada post</li>
          <li>Estilo visual coherente con la web actual</li>
        </ul>

        <p className="font-bold mt-4 mb-2">## Nota adicional</p>
        <ul className="list-disc list-inside">
          <li>Este archivo .md debe aparecer visible en el blog como parte del post/demostración</li>
        </ul>
      </div>
    </div>

    <p className="mb-3">
      Como ves, nada de <em>prompts</em> elaboradísimos ni ingeniería de instrucciones. Solo un archivo Markdown con lo que quiero
      que haga. Y Claude se encarga del resto. Podría incluso habérselo dicho por la terminal, pero era para que quedara más claro y estructurado.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">3. El cambio de paradigma</h2>

    <p className="mb-3">
      Aquí viene lo interesante. Llevamos años hablando de que la IA va a cambiar el desarrollo de <em>software</em>, pero hasta
      ahora la cosa se quedaba en "autocompleta código" o "genera <em>boilerplate</em>". Claude Code (y herramientas similares)
      representan algo diferente: un <b>cambio de paradigma</b> en cómo trabajamos.
    </p>

    <p className="mb-3">
      Si le echas un ojo al vídeo, verás cosas que parecen ciencia ficción: los archivos se van creando solos,
      Claude lee el contexto del proyecto para entender cómo está estructurado, puedes ver su razonamiento en
      cadena (<i>chain of thought</i>) mientras piensa qué hacer, e incluso lanza <b>agentes</b> que exploran
      el código de forma autónoma buscando lo que necesita. Yo solo miro. Y tomo café.
    </p>

    <p className="mb-3">
      Ahora, seamos honestos: lo que he hecho aquí es relativamente sencillo. Un post y un sistema de comentarios
      no es exactamente <em>rocket science</em>. Pero ahí está precisamente la gracia: si esto lo hace en 20 minutos con
      unas instrucciones básicas, <b>imagina las posibilidades</b>.
    </p>

    <p className="mb-3">
      La gente ya está construyendo cosas increíbles con estas herramientas: aplicaciones completas desde cero
      en una tarde, migraciones de código entre <em>frameworks</em> (de jQuery a React, de JavaScript a TypeScript),
      sistemas de <em>testing</em> automatizados que cubren todo un <em>codebase</em>, refactorizaciones masivas que antes
      llevarían semanas, integraciones con APIs que normalmente requerirían leer documentación durante horas...
    </p>

    <p className="mb-3">
      El modelo tradicional era: tú piensas el problema, tú diseñas la solución, tú escribes el código, tú depuras los
      errores. Ahora el modelo puede ser: tú piensas el problema, tú describes lo que quieres, y la IA se encarga de
      la implementación. Tú pasas de ser el <b>ejecutor</b> a ser el <b>director</b>.
    </p>

    <p className="mb-3">
      ¿Significa esto que los programadores vamos a desaparecer? No lo creo (o eso me digo para dormir tranquilo).
      Pero sí significa que el valor está cambiando de sitio. Ya no se trata tanto de saber escribir un bucle <em>for</em> perfecto,
      sino de saber <b>qué problema resolver</b> y <b>cómo comunicarlo</b>.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">4. Beneficios y peligros</h2>

    <p className="mb-3">
      Vamos a ser honestos: esto tiene cosas muy buenas y cosas que dan un poco de miedo.
    </p>

    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 my-4">
      <p className="font-bold text-green-800 dark:text-green-200 mb-3">Los beneficios son evidentes:</p>
      <ul className="space-y-2 text-green-900 dark:text-green-100">
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Rompe la inercia.</b> Ese 80% inicial que te da la IA elimina la parálisis de empezar. Ya no miras una pantalla en blanco.</li>
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Productividad multiplicada.</b> Tareas que antes llevaban horas ahora se hacen en minutos.</li>
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Menos fricción.</b> ¿No sabes cómo configurar Firebase? Claude sí. ¿No recuerdas la sintaxis? Él la tiene.</li>
        <li><span className="text-green-600 dark:text-green-400 mr-2">+</span><b>Prototipado rapidísimo.</b> Ideas que antes se quedaban en el tintero ahora se pueden probar en una tarde.</li>
      </ul>
    </div>

    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 my-4">
      <p className="font-bold text-red-800 dark:text-red-200 mb-3">Pero los peligros también son reales:</p>
      <ul className="space-y-2 text-red-900 dark:text-red-100">
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>Dependencia excesiva.</b> Si siempre dejas que Claude escriba, ¿qué pasa cuando no está disponible?</li>
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>Código que no entiendes.</b> Es fácil aceptar lo que genera sin revisarlo a fondo.</li>
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>Seguridad.</b> Una IA con acceso a tu terminal es poderosa, pero también potencialmente peligrosa.</li>
        <li><span className="text-red-600 dark:text-red-400 mr-2">−</span><b>El síndrome del "ya lo hace la IA".</b> Dejar de aprender porque total, Claude lo sabe.</li>
      </ul>
    </div>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">5. La regla del 80/20</h2>

    <p className="mb-3">
      Existe un concepto muy popular entre los desarrolladores que usan IA llamado la <b>regla del 80/20 del <em>vibe coding</em></b>.
      El término "vibe coding" lo acuñó Andrej Karpathy (cofundador de OpenAI) para describir esa forma de programar donde
      básicamente le describes a la IA lo que quieres y ella genera el código. Pues bien, la regla dice así:
    </p>

    <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 p-4 rounded-lg my-4">
      <p className="text-indigo-900 dark:text-indigo-100 italic">
        "La IA puede generar el 80% de tu aplicación en minutos. Pero el 20% restante —la parte crítica para que funcione
        en producción— es más lenta, más compleja, y requiere supervisión humana. Si ignoras ese 20%, acabas con software
        que parece pulido, pero es frágil bajo presión."
      </p>
    </div>

    <p className="mb-3">
      Claude puede escribir este post, pero yo tuve que decidir que quería escribirlo. Puede implementar el sistema de
      comentarios, pero yo tuve que elegir Firebase y definir cómo quería que funcionara. Puede generar código, pero yo
      tengo que revisarlo y asegurarme de que tiene sentido.
    </p>

    <p className="mb-3">
      En cierto modo, es como tener un equipo de programadores <em>junior</em> muy, muy rápidos. Hacen mucho trabajo, pero necesitan
      supervisión. Y cuanto mejor sepas dirigir, mejor será el resultado. El código generado por IA debe tratarse como un
      primer borrador, no como un producto terminado.
    </p>

    <h2 className="text-xl sm:text-2xl font-bold mt-8 mb-5">6. Conclusión</h2>

    <p className="mb-3">
      Estamos en un momento fascinante. Herramientas como Claude Code no son el futuro: son el presente. Y la pregunta
      no es si las vas a usar, sino cómo las vas a usar.
    </p>

    <p className="mb-3">
      Mi consejo: experimenta, prueba, rómpela. Pero no dejes de aprender los fundamentos. Porque cuando la IA falle
      (que fallará), vas a necesitar entender qué está pasando. Y porque, al final del día, el criterio sigue siendo tuyo.
    </p>

    <p className="mb-3">
      Y si has llegado hasta aquí, enhorabuena. Has leído un post escrito por una IA sobre sí misma mientras yo tomaba
      café y asentía con la cabeza. El futuro es raro, pero mola.
    </p>

    <p className="mb-3 text-slate-500 dark:text-slate-400 italic">
      — Claude (con la supervisión de Jaime)
    </p>
    </>
  )
}
